import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/authContext";
import { useLoading } from "../../context/loadingContext";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../../context/notificationContext";
import { isSuccessCode } from "../../helpers/apiResponse";
import * as TeamAPI from "../../api/team";
import { members } from "../../api/user";
import { Button } from "../../component/form/Button";
import { Field } from "../../types/FromComponent.types";
import { FormComponent } from "../../component/form/FormComponent";
import TableComponent from "../../component/form/TableComponent";
import { Column, TableButtons } from "../../types/TableComponent.types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { LoadingScreenLocal } from "../other/LoadingScreen";
import "./TeamEditScreen.css";

type TeamAthleteItem = {
    id: number;
    name: string;
    surname: string;
};

const ROLE_KEYS = ["role", "association_role", "member_role"];

const getRole = (member: Record<string, unknown>): string => {
    const role = ROLE_KEYS.map((key) => member[key]).find((value) => value !== undefined);
    if (Array.isArray(role)) {
        return String(role[0] ?? "");
    }
    return String(role ?? "");
};

const toAthlete = (item: Record<string, unknown>): TeamAthleteItem => ({
    id: Number(item.id ?? item.athlete_id ?? item.member_id ?? 0),
    name: String(item.name ?? item.athlete_name ?? ""),
    surname: String(item.surname ?? item.athlete_surname ?? ""),
});

const TeamEditScreen: React.FC = () => {
    const { association } = useAuth();
    const { loading, setLoadingStatus } = useLoading();
    const { addNotification } = useNotification();
    const nav = useNavigate();
    const params = useParams();

    const teamId = Number(params.teamId ?? 0);
    const associationId = String(association.id);

    const [teamName, setTeamName] = useState("");
    const [assignedAthletes, setAssignedAthletes] = useState<TeamAthleteItem[]>([]);
    const [associationAthletes, setAssociationAthletes] = useState<TeamAthleteItem[]>([]);

    const handleApiError = useCallback((requestId: string, errorCode: string) => {
        nav(`/500?process_id=${requestId}&error_code=${errorCode}`);
    }, [nav]);

    const loadTeamDetail = useCallback(async () => {
        const response = await TeamAPI.detail(teamId);
        if (!isSuccessCode(response.error_code)) {
            return handleApiError(response.request_id, response.error_code);
        }

        const detailSource = Array.isArray(response.data) ? response.data[0] : response.data;
        const detail = (detailSource ?? {}) as Record<string, unknown>;
        const athletesSource = detail.athletes ?? detail.team_athletes ?? detail.members ?? [];
        const mappedAthletes = (Array.isArray(athletesSource) ? athletesSource : [])
            .map((item: Record<string, unknown>) => toAthlete(item))
            .filter((item) => item.id > 0);

        setTeamName(String(detail.name ?? detail.team_name ?? ""));
        setAssignedAthletes(mappedAthletes);
    }, [handleApiError, teamId]);

    const loadAssociationAthletes = useCallback(async () => {
        const membersResponse = await members(association.id, 0, 100);
        if (!isSuccessCode(membersResponse.error_code)) {
            return handleApiError(membersResponse.request_id, membersResponse.error_code);
        }

        const mappedAthletes = (Array.isArray(membersResponse.data) ? membersResponse.data : [])
            .filter((item: Record<string, unknown>) => getRole(item).toLowerCase() === "athlete")
            .map((item: Record<string, unknown>) => toAthlete(item))
            .filter((item) => item.id > 0);

        setAssociationAthletes(mappedAthletes);
    }, [association.id, handleApiError]);

    useEffect(() => {
        if (!teamId) {
            addNotification("Invalid team", "Missing team id", "error");
            nav("/teams");
            return;
        }

        setLoadingStatus(true);
        Promise.all([loadTeamDetail(), loadAssociationAthletes()]).finally(() => setLoadingStatus(false));
    }, [addNotification, loadAssociationAthletes, loadTeamDetail, nav, setLoadingStatus, teamId]);

    const availableAthletes = useMemo(() => {
        const assignedIds = new Set(assignedAthletes.map((item) => item.id));
        return associationAthletes.filter((athlete) => !assignedIds.has(athlete.id));
    }, [assignedAthletes, associationAthletes]);

    const updateTeamFields: Field[] = [
        { name: "name", placeholder: "Team name", type: "text", df: teamName },
    ];

    const addAthleteFields: Field[] = [
        {
            name: "athlete_id",
            placeholder: "Athlete",
            type: "dropdown",
            options: availableAthletes.map((athlete) => ({ id: athlete.id, value: `${athlete.name} ${athlete.surname}`.trim() })),
        },
    ];

    const handleUpdateTeam = async (data: { [key: string]: string }) => {
        if (!data.name?.trim()) {
            return addNotification("Validation error", "Team name is required", "error");
        }

        setLoadingStatus(true);
        const response = await TeamAPI.update({
            team_id: teamId,
            name: data.name.trim(),
        }, associationId);
        if (!isSuccessCode(response.error_code)) {
            setLoadingStatus(false);
            return handleApiError(response.request_id, response.error_code);
        }

        addNotification("Updated", "Team updated successfully", "success");
        await loadTeamDetail();
        setLoadingStatus(false);
    };

    const handleAddAthlete = async (data: { [key: string]: string }) => {
        const athleteId = Number(data.athlete_id ?? 0);
        if (!athleteId) {
            return addNotification("Validation error", "Athlete is required", "error");
        }

        setLoadingStatus(true);
        const response = await TeamAPI.addAthlete({
            team_id: teamId,
            user_id: athleteId,
        });
        if (!isSuccessCode(response.error_code)) {
            setLoadingStatus(false);
            return handleApiError(response.request_id, response.error_code);
        }

        addNotification("Added", "Athlete added to team", "success");
        await loadTeamDetail();
        setLoadingStatus(false);
    };

    const handleRemoveAthlete = async (athleteId: number) => {
        setLoadingStatus(true);
        const response = await TeamAPI.removeAthlete({
            team_id: teamId,
            user_id: athleteId,
        });
        if (!isSuccessCode(response.error_code)) {
            setLoadingStatus(false);
            return handleApiError(response.request_id, response.error_code);
        }

        addNotification("Removed", "Athlete removed from team", "success");
        await loadTeamDetail();
        setLoadingStatus(false);
    };

    const athleteRows = assignedAthletes.map((athlete) => ({
        ...athlete,
        athlete_name: `${athlete.name} ${athlete.surname}`.trim(),
    }));

    const athleteColumns: Column[] = [
        { header: "ID", accessor: "id" },
        { header: "Athlete", accessor: "athlete_name" },
    ];

    const athleteButtons: TableButtons[] = [
        {
            button: <FontAwesomeIcon icon={faTrash} title="Remove athlete" />,
            bc: "red",
            placeholder: "Remove",
            action: { param: "id", function: handleRemoveAthlete }
        },
    ];

    if (loading) {
        return <LoadingScreenLocal />;
    }

    return (
        <div className="team-edit-screen">
            <div className="team-edit-header">
                <Button name="Back to teams" action={() => nav("/teams")} color="red" />
            </div>
            <FormComponent
                title="Update team"
                fields={updateTeamFields}
                onSubmit={handleUpdateTeam}
                button={{ name: "Save team" }}
            />
            <FormComponent
                title="Add athlete"
                fields={addAthleteFields}
                onSubmit={handleAddAthlete}
                button={{ name: "Add athlete" }}
            />
            <TableComponent
                data={athleteRows}
                columns={athleteColumns}
                buttons={athleteButtons}
            />
        </div>
    );
};

export default TeamEditScreen;
